import { PureComponent } from 'react';

// To handle error messages
import { getErrorMessage } from 'helpers';

// Material UI components / helpers
import { Button, Divider, Typography, withStyles } from '@material-ui/core';
import { PublishRounded as SubmitIcon } from '@mui/icons-material';
// Skeleton component
import { Skeleton } from '@material-ui/lab';
import { UpdateTopicDialogue } from 'components';
// Snackbar provider
import { withSnackbar } from 'notistack';

// Authentication
import { withAuth } from 'services/auth';

// Component styles
import styles from './styles';
import AudioModal from 'views/admin/MaterialInfo/components/CourseTable/AudioModal';
import PdfModal from './PdfModal';

class BookingInfo extends PureComponent {
  constructor(props) {
    super(props);

    // Initialize the state
    this.state = {
      material: undefined,
      course: undefined,
      isAudioModalOpen: false,
      currentAudioSrc: null,
      lessonName: null,
      isPdfModalOpen: false,
      currentPdfUrl: null,
      isUpdateDialogOpen: false
    };
    this.handleOpenAudioModal = this.handleOpenAudioModal.bind(this);
    this.handleCloseAudioModal = this.handleCloseAudioModal.bind(this);
    this.handleOpenPdfModal = this.handleOpenPdfModal.bind(this);
    this.handleClosePdfModal = this.handleClosePdfModal.bind(this);
    this.handleOpenUpdateDialog = this.handleOpenUpdateDialog.bind(this);
    this.handleCloseUpdateDialog = this.handleCloseUpdateDialog.bind(this);
    // Set the ready flag to true
    this.ready = true;
  }
  handleOpenAudioModal = (audioSrc, name) => {
    this.setState({
      isAudioModalOpen: true,
      currentAudioSrc: audioSrc,
      lessonName: name
    });
  };

  handleCloseAudioModal = () => {
    this.setState({
      isAudioModalOpen: false,
      currentAudioSrc: null
    });
  };

  handleOpenPdfModal = (pdfUrl, name) => {
    this.setState({
      isPdfModalOpen: true,
      currentPdfUrl: pdfUrl,
      lessonName: name
    });
  };

  handleClosePdfModal = () => {
    this.setState({
      isPdfModalOpen: false,
      currentPdfUrl: null
    });
  };

  handleOpenUpdateDialog = () => {
    this.setState({
      isUpdateDialogOpen: true
    });
  };

  handleCloseUpdateDialog = async (topicData) => {
    const { auth, enqueueSnackbar, booking, onBookingUpdate } = this.props;

    try {
      const bookingId = booking.id;
      const tutorId = booking.tutor;
      const isPteLesson = booking?.info?.topic?.material === 'pte';

      // Update the booking topic
      await auth.api.bookings.updateBooking(tutorId, bookingId, {
        ...booking.info,
        topic: topicData.topic
      });
      // Trigger refresh if callback is provided
      if (onBookingUpdate) {
        onBookingUpdate(topicData);
      }
      enqueueSnackbar('Topic updated successfully!', { variant: 'success' });

      // Close the dialog
      this.setState({ isUpdateDialogOpen: false });

      // Force component re-render to refresh materials
      this.componentDidMount();
    } catch (error) {
      console.error('Error updating topic:', error);
      enqueueSnackbar('Failed to update topic. Please try again.', {
        variant: 'error'
      });
    }
  };
  componentDidUpdate(prevProps) {
    const { booking } = this.props;
    const prevBooking = prevProps.booking;
    console.log(booking?.lastUpdated !== prevBooking?.lastUpdated);

    // Re-fetch materials and trigger full re-render
    this.componentDidMount();
  }
  async componentDidMount() {
    // Grab the materials endpoint from the props
    const { auth, info, onResource } = this.props;
    console.log('info', info);
    // Make sure we've been provided with the material info before making any requests
    if (!info) return;

    // Wrap in a try-catch to handle any errors
    try {
      // Retrieve the materials from the API
      const [material, course] = [
        (await auth.api.materials.getMaterials()).find(
          (entry) => entry.id === info.topic.material
        ),
        (await auth.api.materials.getCourses(info.topic.material)).find(
          (entry) => entry.id === info.topic.course
        )
      ];

      // Find the lesson
      const lesson = course?.lessons?.find(
        (lesson) => lesson.id === info.topic.lesson
      );

      // Update the state with the materials
      if (this.ready) {
        this.setState(
          {
            material,
            course,
            lesson
          },
          () => {
            // Trigger the resource handler
            if (onResource) onResource(lesson[`tutorResource`]);
          }
        );
      }
    } catch (error) {
      // If we're mounted, update the state with the error
      if (this.ready) {
        this.setState({
          error: getErrorMessage(error)
        });
      }
    }
  }

  componentWillUnmount() {
    // Update the ready flag to false
    this.ready = false;
  }

  render() {
    // Destructure the props, state
    const {
      classes,
      info,
      auth,
      enqueueSnackbar,
      hideOpenButton,
      booking,
      groupSession,
      isCallComp
    } = this.props;
    const {
      material,
      course,
      lesson,
      error,
      isAudioModalOpen,
      currentAudioSrc,
      lessonName,
      isPdfModalOpen,
      currentPdfUrl,
      isUpdateDialogOpen
    } = this.state;
    console.log({ booking });
    // Destructure the booking information
    const { correctMistakes, introduction, notes } = info || {};

    // Ensure any errors have not occured

    if (error)
      return (
        <Typography variant="subtitle2" color="textSecondary">
          {error}
        </Typography>
      );

    // Render the booking information
    return (
      <div className={classes.details}>
        <div className={classes.detailsHeader}>
          {material ? (
            <>
              <div className={classes.detailsHeaderImgWrapper}>
                <img
                  className={classes.detailsHeaderImg}
                  src={material && material.icon}
                  alt=""
                />
              </div>
              <div className={classes.detailsHeaderInfo}>
                <Typography variant="h5">
                  <b>{material && material.name}</b>
                </Typography>
                <Typography color="textSecondary">
                  <b>{course && `${course.name}`}</b>
                </Typography>
                <Typography color="textSecondary">
                  {lesson && `${lesson.code} - ${lesson.name}`}
                </Typography>
                <Typography color="textSecondary">
                  {lesson && `Level ${lesson.level}`}
                </Typography>
                {!hideOpenButton && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={['student-cancelled', 'tutor-cancelled'].includes(
                      booking?.status
                    )}
                    fullWidth
                    className={classes.detailsHeaderButton}
                    onClick={() => {
                      if (booking && lesson) {
                        if (booking.info.topic.lesson === lesson.id) {
                          // if (
                          //   [
                          //     'completed',
                          //     'booked',
                          //     'in-progress',
                          //     'pending-feedback',
                          //     'open'
                          //   ].includes(booking.status)
                          // ) {
                          const resource = lesson[`tutorResource`];
                          this.handleOpenPdfModal(null, lesson.name);
                          auth.api.materials
                            .getLessonDocument(enqueueSnackbar, resource)
                            .then((pdfUrl) => {
                              if (pdfUrl) {
                                this.setState({ currentPdfUrl: pdfUrl });
                              }
                            });
                          // } else {
                          //   enqueueSnackbar(
                          //     `You cannot open the material due to booking status: ${booking.status}.`,
                          //     {
                          //       variant: 'warning'
                          //     }
                          //   );
                          // }
                        } else {
                          enqueueSnackbar(
                            'This material cannot be opened for the current lesson.',
                            {
                              variant: 'warning'
                            }
                          );
                        }
                      } else {
                        enqueueSnackbar(
                          'Cannot open material. Missing booking or lesson information.',
                          {
                            variant: 'error'
                          }
                        );
                      }
                    }}>
                    Open Material
                  </Button>
                )}
                {!hideOpenButton && lesson?.audioResource && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    disabled={['student-cancelled', 'tutor-cancelled'].includes(
                      booking?.status
                    )}
                    className={classes.detailsHeaderButton}
                    onClick={() => {
                      if (booking && lesson) {
                        if (booking.info.topic.lesson === lesson.id) {
                          // if (
                          //   [
                          //     'completed',
                          //     'booked',
                          //     'in-progress',
                          //     'pending-feedback',
                          //     'open'
                          //   ].includes(booking.status)
                          // ) {
                          this.handleOpenAudioModal(
                            lesson?.audioResource,
                            lesson?.name
                          );
                          // } else {
                          //   enqueueSnackbar(
                          //     `You cannot open the material due to booking status: ${booking.status}.`,
                          //     {
                          //       variant: 'warning'
                          //     }
                          //   );
                          // }
                        } else {
                          enqueueSnackbar(
                            'This material cannot be opened for the current lesson.',
                            {
                              variant: 'warning'
                            }
                          );
                        }
                      } else {
                        enqueueSnackbar(
                          'Cannot open material. Missing booking or lesson information.',
                          {
                            variant: 'error'
                          }
                        );
                      }
                    }}>
                    open Audios
                  </Button>
                )}
                {isCallComp && !groupSession && auth?.role === 'tutor' && (
                  <Button
                    size="small"
                    variant="contained"
                    className={classes.gutterRight}
                    onClick={this.handleOpenUpdateDialog}
                    startIcon={<SubmitIcon />}>
                    Update Material
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={classes.detailsHeaderImgWrapper}>
                <Skeleton
                  className={classes.detailsHeaderImg}
                  variant="circle"
                />
              </div>
              <div className={classes.detailsHeaderInfo}>
                <Typography variant="h5">
                  <Skeleton width="60%" />
                </Typography>
                <Typography>
                  <Skeleton width="80%" />
                </Typography>
                <Typography>
                  <Skeleton width="70%" />
                </Typography>
                <Typography>
                  <Skeleton width="40%" />
                </Typography>
                {!hideOpenButton && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    className={classes.detailsHeaderButton}
                    disabled>
                    open material
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
        <Divider className={classes.detailsHeaderDivider} />

        {!groupSession && (
          <>
            <Typography color="textSecondary">
              {introduction
                ? 'Start the lessons with introductions'
                : 'Skip introductions, get straight into the lesson'}
            </Typography>
            <Typography color="textSecondary" gutterBottom>
              {correctMistakes
                ? 'Actively correct my mistakes during the lesson'
                : 'Do not actively correct my mistakes'}
            </Typography>
          </>
        )}

        {notes && (
          <>
            <Divider className={classes.detailsHeaderDivider} />
            <Typography color="textSecondary">
              <b>Notes: </b>
              {notes}
            </Typography>
          </>
        )}
        <AudioModal
          open={isAudioModalOpen}
          onClose={this.handleCloseAudioModal}
          audioPaths={currentAudioSrc}
          auth={auth}
          lessonName={lessonName}
        />
        <PdfModal
          open={isPdfModalOpen}
          onClose={this.handleClosePdfModal}
          pdfUrl={currentPdfUrl}
          lessonName={lessonName}
        />
        {isUpdateDialogOpen && (
          <UpdateTopicDialogue
            auth={auth}
            booking={booking}
            onSubmit={(topicData) => {
              this.handleCloseUpdateDialog(topicData);
            }}
            onClose={() => {
              this.setState({ isUpdateDialogOpen: false });
            }}
          />
        )}
      </div>
    );
  }
}

export default withSnackbar(withAuth(withStyles(styles)(BookingInfo)));
